import {
  FluidFieldValidation,
  FluidFieldValidationType,
  FluidFormElementConfig,
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidFormState,
  FluidInputType,
  FluidLanguage,
  FluidPopoverConfig,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidUpdateStrategy,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  CancelInput,
  FormLabel,
  Validation,
} from '../../common/fluid-common-form.components';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  getFormInputId,
  getValue,
  handleAutoFocus,
  mapConfigToLocal,
  markTouchedAndValidate,
  reset,
  setValue,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import {
  elementClass,
  setInitialElementState,
} from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Datepicker
 *
 * @advancedUsage /forms
 *
 * @builder true
 *
 */
@Component({
  tag: 'fluid-datepicker',
  styleUrl: 'fluid-datepicker.less',
  shadow: true,
})
export class FluidDatepicker
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Validation Variables

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <input> element.
   */
  _elementRef: HTMLInputElement;

  /**
   * Boolean that tracks whether the form field has received autofocus
   */
  _autoFocus = false;

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  /**
   * Boolean that tracks whether the form field currently has :focus.
   */
  @State() _hasFocus: boolean;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- Props

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A label to show with the input field (where applicable)
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * If provided, Datepicker's Form Label will render a "helptext" icon
   * with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Optional placeholder to add to the field
   */
  @Prop({ mutable: true }) placeholder: string;

  /**
   * Should the field be disabled or not
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * Should the field be readonly and have readonly
   * styles applied
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * What is the initial value of the field (where applicable)
   */
  @Prop({ mutable: true }) initialValue: string;

  /**
   * An array of field validation options
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * The type of the input field
   */
  @Prop({ mutable: true }) type: FluidInputType;

  /**
   * A muted label to show after the main form label
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * If the field is an inline one, remove all margins so it sits
   * nicely in its container.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * If true, allows the clear icon to be shown for non-required fields
   */
  @Prop({ mutable: true }) allowCancel?: boolean;

  /**
   * The earliest date to accept.
   * If the value entered into the element occurs beforehand, the element fails constraint validation.
   * If the value of the min attribute isn't a possible date string in the format `yyyy-mm-dd`, then the element has no minimum date value.
   */
  @Prop({ mutable: true }) minDate: string | number = '';

  /**
   * The latest date to accept.
   * If the value entered into the element occurs afterward, the element fails constraint validation.
   * If the value of the max attribute isn't a possible date string in the format `yyyy-mm-dd`, then the element has no maximum date value.
   * By default we are capping the year to 4 digits
   */
  @Prop({ mutable: true }) maxDate: string | number = '9999-12-31';

  /**
   * Allows a unique identifier to be passed to be prepended to test IDs
   */
  @Prop({ mutable: true }) identifier: string;

  /**
   * Allows form element configuration to be passed as a single property.
   */
  @Prop({ mutable: true }) config: FluidFormElementConfig;

  @Watch('config')
  onConfigChanged() {
    mapConfigToLocal(this, true);
  }

  /**
   * If true, this field will automatically have focus on page load. Note: only one form field should have this
   * autoFocus set to true on a page. If multiple fields have autoFocus = true, then there could be unexpected behaviour.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * If true, will display the calendar popup when the click event is trigger
   */
  @Prop({ mutable: true }) showPickerOnClick = false;

  /**
   * Determines the updating strategy for the field;
   * 'change' - the field will update the element state on each change to its value.
   * 'blur' - the field will update the element state when the user leaves the field.
   */
  @Prop({ mutable: true }) updateStrategy: FluidUpdateStrategy =
    FluidUpdateStrategy.INPUT;

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when its value changes
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    mapConfigToLocal(this);
    this.initializeValidation();
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Set the initial element state on first load
   */
  componentWillLoad() {
    this._elementState = setInitialElementState(
      this.initialValue || '',
      this.controlName
    );
    this._autoFocus = this.autoFocus;
  }

  componentDidRender() {
    handleAutoFocus(this);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    return setValue(this, value, null, opts);
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    return reset(this, clear);
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  initializeValidation(): void {
    const invalidDateValidation = {
      type: FluidFieldValidationType.CUSTOM,
      validatorFn: () => this._elementRef.checkValidity(),
      message: 'Please enter a valid date',
    } as unknown as FluidFieldValidation;

    this.validation = [invalidDateValidation, ...(this.validation ?? [])];
  }

  _onClickHandler(e) {
    if (!this.showPickerOnClick) return;
    e.target.showPicker();
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const styles = elementClass(
      'fluid-datepicker',
      this.theme,
      this._elementState,
      this.inlineField,
      this.touched,
      {
        disabled: this.disabled,
        readonly: this.readonly,
      }
    );

    return (
      <div
        class={styles.componentWrapper}
        data-test-id={`${this.identifier}_wrapper`}>
        <div
          class={styles.formGroupClass}
          data-test-id={`${this.identifier}_form_group`}>
          <FormLabel host={this} />
          <div
            class={'input-wrapper'}
            data-test-id={`${this.identifier}_input_wrapper`}>
            {/*
             * Regarding the clear button functionality, seems that the property `required` will affect the standard behavior.
             * For future implementation, do not add this property.
             */}
            <input
              id={getFormInputId(this.config?.id, this.controlName)}
              data-test-id={`${this.identifier}_input_field`}
              ref={(el) => (this._elementRef = el)}
              class={styles.inputClass}
              type={
                this.placeholder === ' ' &&
                !this._elementState.value &&
                !this._hasFocus
                  ? 'text'
                  : 'date'
              }
              name={this._elementState.name}
              onClick={this._onClickHandler.bind(this)}
              onChange={(event: InputEvent) => {
                if (
                  this.updateStrategy?.toUpperCase() !==
                  FluidUpdateStrategy.BLUR
                ) {
                  update(this, event);
                }
              }}
              onBlur={(event: FocusEvent) => {
                if (
                  this.updateStrategy?.toUpperCase() ===
                  FluidUpdateStrategy.BLUR
                ) {
                  update(this, event);
                }
                this.markTouchedAndValidate().then(
                  () => (this._hasFocus = false)
                );
              }}
              onFocus={() => (this._hasFocus = true)}
              placeholder={translateProperty.bind(this)('placeholder')}
              disabled={this.disabled}
              readonly={this.readonly}
              min={this.minDate}
              max={this.maxDate}
              value={this._elementState.value}
            />
            <CancelInput host={this} />
          </div>
          <div
            class={{
              'form-element-notifications': true,
              'inline-field': this.inlineField,
            }}>
            <Validation host={this} fluidTranslate={this.fluidTranslate} />
          </div>
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;
}
